import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { LiveClock, StatusIndicator } from '../components/LiveClock';
import { HATS, COLORS, ASCII_VARIANTS, generateVisitorName } from '../data/visitors';
import { createVisitor, updateVisitor, getOwnToken, setOwnVisitorId, getOwnVisitorId, fetchVisitors, setOwnVisitorColor } from '../api/visitorsApi';
import { PenTool, Eraser } from 'lucide-react';

function DrawableCard({ colorValue, name, signature, onChange, onClear }) {
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const [drawing, setDrawing] = useState(false);
  const [mode, setMode] = useState('pen'); // 'pen' or 'relative rounded-lg p-6 overflow-hidden'
  const lastPt = useRef(null);

  useEffect(() => {
    if (!canvasRef.current) return;
    const canvas = canvasRef.current;
    const container = containerRef.current;
    
    // Set canvas size to match container
    const rect = container.getBoundingClientRect();
    canvas.width = rect.width;
    canvas.height = rect.height;
    
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#ffffff';
    
    // Redraw existing signature if any
    if (signature) {
      const img = new window.Image();
      img.onload = () => ctx.drawImage(img, 0, 0);
      img.src = signature;
    }
  }, [signature]);

  const start = (e) => {
    setDrawing(true);
    const rect = canvasRef.current.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    lastPt.current = { x, y };
  };

  const move = (e) => {
    if (!drawing) return;
    e.preventDefault();
    const rect = canvasRef.current.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    const ctx = canvasRef.current.getContext('2d');
    
    if (mode === 'eraser') {
      ctx.clearRect(x - 8, y - 8, 16, 16);
    } else {
      ctx.beginPath();
      ctx.moveTo(lastPt.current.x, lastPt.current.y);
      ctx.lineTo(x, y);
      ctx.stroke();
    }
    lastPt.current = { x, y };
  };

  const end = () => {
    if (!drawing) return;
    setDrawing(false);
    onChange(canvasRef.current.toDataURL('image/png'));
  };

  return (
    <div className="relative w-full max-w-md pop-in">
      <div 
        ref={containerRef}
        className="drawable-card relative p-6 overflow-hidden"
        style={{ background: colorValue, borderRadius: '36px' }}
      >
        {/* Card Content */}
        <div className="pointer-events-none relative z-10">
          <div className="pt-4 border-white/30">
            <div className=" text-2xl text-white mb-3">Manisha&rsquo;s World</div>
            <div className="text-[13px] text-white/85 space-y-1">
              <div><span className="text-white/50">VISITOR</span></div>
              <div className="uppercase font-bold text-[14px]">{name}</div>
              <div className="mt-2"><span className="text-white/50">ISSUED ON</span></div>
              <div className="text-[13px]">{new Date().toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' })}</div>
              <div className="mt-3 text-[13px]">NO. {Math.floor(Math.random() * 10000)}</div>
            </div>
            {/* Signature line indicator */}
            <div className="flex items-center justify-end gap-1 text-white text-[12px]">
              <span style={{ fontSize: '15px', marginRight: '8px' }}>Sign here:</span>
              <div className="border-b border-white/60 w-40"></div>
            </div>
          </div>
        </div>

        {/* Drawable Canvas Overlay */}
        <canvas
          ref={canvasRef}
          className={`absolute inset-0 rounded-[36px] touch-none z-20 ${
            mode === 'eraser' ? 'drawable-canvas--eraser' : 'drawable-canvas--pen'
          }`}
          onMouseDown={start}
          onMouseMove={move}
          onMouseUp={end}
          onMouseLeave={end}
          onTouchStart={start}
          onTouchMove={move}
          onTouchEnd={end}
        />
        <div
        className="absolute inset-0 opacity-20 pointer-events-none"
        style={{
          backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.5) 1px, transparent 1px)',
          backgroundSize: '10px 10px',
        }}
      />
      </div>

      {/* Controls */}
      <div className="flex gap-3 mt-3 justify-center">
        <button
          onClick={() => setMode('pen')}
          className={`flex items-center gap-2 px-3 py-2 rounded border transition-colors text-[12px] uppercase tracking-wider ${
            mode === 'pen'
              ? 'bg-[#333] text-white border-[#333]'
              : 'border-[#ddd] text-[#666] hover:text-[#333] hover:border-[#999] bg-white'
          }`}
          title="Draw mode"
        >
          <PenTool className="w-4 h-4" />
        </button>
        <button
          onClick={() => setMode('eraser')}
          className={`flex items-center gap-2 px-3 py-2 rounded border transition-colors text-[12px] uppercase tracking-wider ${
            mode === 'eraser'
              ? 'bg-[#333] text-white border-[#333]'
              : 'border-[#ddd] text-[#666] hover:text-[#333] hover:border-[#999] bg-white'
          }`}
          title="Erase mode"
        >
          <Eraser className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

export default function OnboardingPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const editing = params.get('edit') !== null;
  const fromGallery = params.get('from') === 'gallery';
  const fromIntro = params.get('from') === 'intro';

  const [hat, setHat] = useState(HATS[0].name);
  const [colorIdx, setColorIdx] = useState(0);
  const [variant, setVariant] = useState(0);
  const [signature, setSignature] = useState(null);
  const [name, setName] = useState(() => generateVisitorName());
  const [editId, setEditId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!editing) return;
    (async () => {
      try {
        const ownId = getOwnVisitorId();
        if (!ownId) return;
        const visitors = await fetchVisitors();
        const own = visitors.find(v => v.id === ownId);
        if (own) {
          setEditId(own.id);
          setHat(own.hat);
          const ci = COLORS.findIndex(c => c.name === own.color);
          if (ci >= 0) setColorIdx(ci);
          setOwnVisitorColor(own.color_value || COLORS[ci >= 0 ? ci : 0].value);
          setVariant(own.variant);
          setSignature(own.signature);
          setName(own.name);
        }
      } catch (_e) {}
    })();
  }, [editing]);

  const generateName = () => {
    setName(generateVisitorName());
  };

  const skip = () => {
    if (fromIntro) {
      navigate('/home');
    } else {
      navigate('/visitor-gallery');
    }
  };

  const finish = async () => {
    setError(null);
    setSubmitting(true);
    try {
      const color = COLORS[colorIdx];
      setOwnVisitorColor(color.value);
      if (editing && editId) {
        await updateVisitor(editId, { hat, color: color.name, variant, signature });
      } else {
        const visitor = await createVisitor({
          name,
          hat,
          color: color.name,
          variant,
          signature,
          own_token: getOwnToken(),
        });
        setOwnVisitorId(visitor.id);
        if (visitor.color_value) setOwnVisitorColor(visitor.color_value);
      }
      // Route based on where they came from
      if (fromIntro) {
        navigate('/home');
      } else {
        navigate('/visitor-gallery');
      }
    } catch (e) {
      setError(e?.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen w-full relative overflow-hidden flex flex-col px-4 py-12 bg-[#f3f1eb] justify-center">
      {/* Header with back button */}
      <div className="relative z-10 w-full flex justify-between items-center" style={{ position: 'absolute', top: 20 }}>
        <button onClick={() => navigate((fromGallery || editing) ? '/visitor-gallery' : (fromIntro ? '/' : '/'))} className="flex items-center gap-1 text-[14px] uppercase tracking-wider text-[#666] hover:text-[#333]">
          ← Back
        </button>
        <div className="flex-1" />
      </div>

      {/* Main content */}
      <div className="relative z-10 flex flex-col items-center w-full max-w-6xl mx-auto">
        {/* Welcome section */}
        <div className="text-center mb-8 fade-slide-in mt-4">
          <p className=" text-[14px] uppercase tracking-[0.2em] text-[#242424]">Welcome, visitor.</p>
          <p className=" text-[14px] uppercase tracking-[0.2em] text-[#666] mt-1">I hope you enjoy your time here.</p>
        </div>

        {/* Two-column layout: Name input + Preview on left, options on right */}
        <div className="grid grid-cols-1 gap-8 w-full">
          
          {/* Left: Name and Drawable Card Preview */}
          <div className="flex flex-col items-center">
            {/* Name input with regenerate button */}
            <div className="flex items-center gap-3 mb-8 w-full max-w-md">
              <label className=" text-[14px] uppercase tracking-wider text-[#666]">Name:</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="flex-1 bg-white border border-[#ddd] rounded px-3 py-2 text-[13px] text-[#333] placeholder-[#999] focus:outline-none focus:border-[#999]"
                placeholder="Enter your name"
              />
              <button
                onClick={generateName}
                className="px-3 py-1 rounded border border-[#ddd] text-[#666] hover:text-[#333] hover:border-[#999] transition-colors bg-white"
                title="Generate random name"
              >
                ⟳
              </button>
            </div>

            {/* Drawable Card */}
            <DrawableCard 
              colorValue={COLORS[colorIdx].value}
              name={name}
              signature={signature}
              onChange={setSignature}
              onClear={() => setSignature(null)}
            />
          </div>

          {/* Right: Customization Options */}
          <div className="flex flex-col gap-8">
            
            {/* Color Selection */}
            <div>
              <p className=" text-[12px] uppercase tracking-wider text-[#8b7d76] mb-3 flex justify-center">
                Card Color
              </p>
              <div className="flex gap-3 justify-center">
                {COLORS.map((c, i) => (
                  <button
                    key={c.name}
                    onClick={() => {
                      setColorIdx(i);
                      setOwnVisitorColor(c.value);
                    }}
                    className={`w-10 h-10 rounded-full border-3 transition-all hover:scale-110 ${
                      colorIdx === i ? 'border-[#666] scale-110 ring-2 ring-[#666]' : 'border-[#666]'
                    }`}
                    style={{ background: c.value }}
                    title={c.name}
                  />
                ))}
              </div>
            </div>

          </div>

        </div>

        {/* Error message */}
        {error && <div className="mt-6 text-rose-600 text-[11px] uppercase text-center">{error}</div>}

        {/* Enter / Skip */}
        <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
          <button
            type="button"
            disabled={submitting}
            onClick={finish}
            className="btn-magnetic btn-sheen px-8 py-3 rounded-lg bg-[#333] text-white text-[13px] uppercase tracking-wider hover:bg-[#555] disabled:opacity-60"
          >
            {submitting ? 'Signing\u2026' : (editing ? 'Save changes' : 'Enter')}
          </button>
          <button
            type="button"
            disabled={submitting}
            onClick={skip}
            className="btn-magnetic btn-sheen px-8 py-3 rounded-lg border border-[#ccc] bg-transparent text-[#666] text-[13px] uppercase tracking-wider hover:text-[#333] hover:border-[#999] transition-colors disabled:opacity-60"
          >
            Skip
          </button>
        </div>
        <p className="mt-10 mb-10 text-[14px] uppercase tracking-wider text-[#666] text-center">
          Your card will appear in the visitor gallery after review.
        </p>
      </div>

      <div className="absolute bottom-5 left-6 z-10">
        <LiveClock className="text-[11.5px] text-[#8b7d76] block" />
        <StatusIndicator className="mt-1 text-[#a89e96]" />
      </div>
    </div>
  );
}
